/**
 * CelluleMembersModal — gestion des membres assignés à une cellule.
 * - Liste les membres actuels avec bouton Retirer
 * - Recherche fuzzy (Fuse.js) sur les membres de la même église, hors cellule
 * - Clic sur un résultat → assignation immédiate
 */
import { useState, useEffect, useMemo, useCallback } from "react";
import Fuse from "fuse.js";
import Modal from "./Modal";
import Input from "./Input";
import Avatar from "./Avatar";
import Button from "./Button";
import { colors, fonts } from "../styles/tokens";
import { cellulesApi } from "../services/api";
import useMembers from "../hooks/useMembers";
import useToast from "../hooks/useToast";

export default function CelluleMembersModal({
  open,
  onClose,
  cellule,        // { id, nom, eglise_id, eglise_nom }
  onChange,       // callback appelé après chaque ajout/retrait
}) {
  const { members } = useMembers();
  const toast = useToast();
  const [assigned, setAssigned] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState(null);

  /** Charge les membres actuellement assignés à la cellule. */
  const reload = useCallback(async () => {
    if (!cellule?.id) return;
    setLoading(true);
    try {
      const { data } = await cellulesApi.membres(cellule.id);
      setAssigned(data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [cellule?.id, toast]);

  useEffect(() => {
    if (open) reload();
    else { setSearch(""); setAssigned([]); }
  }, [open, reload]);

  /** Membres assignables : même église, pas déjà dans la cellule. */
  const availableMembers = useMemo(() => {
    if (!cellule) return [];
    const assignedIds = new Set(assigned.map((m) => m.id));
    return members.filter(
      (m) => m.eglise_id === cellule.eglise_id && !assignedIds.has(m.id)
    );
  }, [members, cellule, assigned]);

  const fuse = useMemo(
    () => new Fuse(availableMembers, {
      keys: ["prenom", "nom", "full_name", "email", "telephone"],
      threshold: 0.35,
      ignoreLocation: true,
      minMatchCharLength: 2,
    }),
    [availableMembers]
  );

  const searchResults = useMemo(() => {
    if (search.trim().length < 2) return [];
    return fuse.search(search.trim()).map((r) => r.item).slice(0, 8);
  }, [search, fuse]);

  const handleAssign = async (member) => {
    if (!cellule) return;
    setBusyId(member.id);
    try {
      await cellulesApi.addMembre(cellule.id, member.id);
      toast.success(`${member.prenom} ${member.nom} ajouté(e) à la cellule`);
      setSearch("");
      await reload();
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'assignation");
    } finally {
      setBusyId(null);
    }
  };

  const handleRemove = async (member) => {
    if (!cellule) return;
    setBusyId(member.id);
    try {
      await cellulesApi.removeMembre(cellule.id, member.id);
      toast.success(`${member.prenom} ${member.nom} retiré(e) de la cellule`);
      await reload();
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={cellule ? `Membres — ${cellule.nom}` : "Membres"}
      subtitle={cellule?.eglise_nom}
      size="lg"
      footer={<Button variant="ghost" onClick={onClose}>Fermer</Button>}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        {/* Recherche d'ajout */}
        <div>
          <div style={{ position: "relative" }}>
            <Input
              label="Ajouter un membre"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Tapez un prénom ou un nom…"
              icon="🔍"
            />

            {searchResults.length > 0 && (
              <div style={{
                marginTop: "10px",
                background: colors.bg,
                border: `1px solid ${colors.border}`,
                borderRadius: "10px",
                overflow: "hidden",
              }}>
                {searchResults.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => handleAssign(m)}
                    disabled={busyId === m.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      width: "100%",
                      padding: "10px 14px",
                      textAlign: "left",
                      background: "transparent",
                      borderBottom: `1px solid ${colors.border}`,
                      transition: "background 120ms",
                      opacity: busyId === m.id ? 0.5 : 1,
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = colors.goldDim; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                  >
                    <Avatar name={`${m.prenom} ${m.nom}`} size="sm" />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: colors.text, fontWeight: 500, fontSize: "14px" }}>
                        {m.prenom} {m.nom}
                      </div>
                      <div style={{ color: colors.textMuted, fontSize: "12px" }}>
                        {m.role_eglise || m.departement || "—"}
                        {m.cellule_nom && ` · actuellement dans ${m.cellule_nom}`}
                      </div>
                    </div>
                    <span style={{ color: colors.gold, fontWeight: 600, fontSize: "13px" }}>+ Ajouter</span>
                  </button>
                ))}
              </div>
            )}

            {search.trim().length >= 2 && searchResults.length === 0 && (
              <div style={{ marginTop: "10px", color: colors.textMuted, fontSize: "13px", padding: "8px 14px" }}>
                Aucun membre trouvé dans cette église.
              </div>
            )}
          </div>
        </div>

        {/* Liste des membres assignés */}
        <div>
          <h3 style={{
            fontFamily: fonts.heading,
            fontSize: "16px",
            fontWeight: 600,
            marginBottom: "10px",
            color: colors.text,
          }}>
            Membres assignés ({assigned.length})
          </h3>

          {loading ? (
            <div style={{ color: colors.textMuted, fontSize: "13px" }}>Chargement...</div>
          ) : assigned.length === 0 ? (
            <div style={{
              padding: "24px",
              background: colors.bg,
              border: `1px dashed ${colors.border}`,
              borderRadius: "10px",
              textAlign: "center",
              color: colors.textMuted,
              fontSize: "13px",
            }}>
              Aucun membre assigné. Utilisez la recherche ci-dessus pour en ajouter.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {assigned.map((m) => (
                <div
                  key={m.id}
                  style={{
                    display: "flex", alignItems: "center", gap: "12px",
                    padding: "10px 12px",
                    background: colors.bg,
                    border: `1px solid ${colors.border}`,
                    borderRadius: "10px",
                  }}
                >
                  <Avatar name={`${m.prenom} ${m.nom}`} size="sm" />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500 }}>{m.prenom} {m.nom}</div>
                    {m.role_eglise && (
                      <div style={{ color: colors.textMuted, fontSize: "12px" }}>{m.role_eglise}</div>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => handleRemove(m)}
                    disabled={busyId === m.id}
                  >
                    Retirer
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
